import { getNumberUnmaskStr } from 'components/FormEditReceiver/func';
import TooltipCommon from 'components/common/TooltipCommon';
import TooltipCommonMobile from 'components/common/TooltipCommon/mobile';
import { DESKTOP_MIN_WIDTH } from 'constants';
import { checkRegexValid, formatter } from 'helpers';
import { Fade } from 'helpers/animation';
import elementHelper from 'helpers/elementHelper';
import useDebounceCallback from 'hooks/useDebounceCallback';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import { isEmpty, isFunction } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { stringUtils } from 'utils';
import withInputFieldFocus from '../../../common/withInputFieldFocus';

const customFormInputStyleMobile = {};

const customFormInputStyle = {};

const FormInputValidationWithFormatAndRegex = ({
  ref,
  field,
  config,
  numberMaskStr,
  setNumberMaskStr,
  countryConfig,
  accountNumberExisteds,
  information,
  setInformation,
  validation,
  setValidation,
  checkAccountExisted,
  setSaveDisableInit,
  styles = {},
  inputStyles = {},
  errorStyles = {},
  isAllowSpecificCharacter = false,
  isDisableTrim = false,
  isDisabled = false,
  isReadOnly = false,
}) => {
  const { t } = useLang();
  const { isMobile, isTablet, isDesktop } = useDetectDevice();

  const isConfigRequired = config?.required;

  const isConfigVisible = config?.visible;

  const isApiEnabled = config?.apiEnabled;

  const placeholder = config?.example;

  const numberFormat = formatter.formatParse(config?.format);

  const numberRegex = config?.regex;

  const numberMaxLength = config?.max;

  const numberMinLength = config?.min;

  const label = config?.name || config?.type || t(field.label);

  const fieldType = elementHelper.detectInputFieldType(numberRegex);

  const [isChecking, setChecking] = useState(false);

  const lastSelfWrittenValueRef = useRef();

  useEffect(() => {
    if (information[field.name] === lastSelfWrittenValueRef.current) {
      return;
    }

    if (information[field.name]) {
      const numberMask = formatter.mask(
        getNumberUnmaskStr(
          information[field.name],
          numberFormat,
          true,
          false,
          isAllowSpecificCharacter,
        ),
        numberFormat,
      );
      setNumberMaskStr(numberMask);
    } else {
      setNumberMaskStr('');
    }
  }, [information[field.name]]);

  useEffect(() => {
    const numberUnmaskStr = getNumberUnmaskStr(
      information[field.name],
      numberFormat,
      true,
      true,
      isAllowSpecificCharacter,
    );
    const isCheckNumber =
      numberUnmaskStr &&
      numberUnmaskStr?.length <= numberMaxLength &&
      numberUnmaskStr?.length >= numberMinLength &&
      !validation[field.name];

    if (isCheckNumber && isChecking) {
      handleNumberValid(numberUnmaskStr, validation, information);
    }
  }, [isChecking, information]);

  const checkNumberValid = async (value, validation, information) => {
    let newValidation = { ...validation };
    if (value?.length >= numberMinLength && value?.length <= numberMaxLength) {
      if (
        numberRegex &&
        !checkRegexValid(
          stringUtils.replaceAllCharacter(value, isAllowSpecificCharacter),
          numberRegex,
        )
      ) {
        newValidation = {
          ...newValidation,
          [field.name]: field?.msgNumberInvalid || t('form_invalid_6'),
        };
        setValidation(newValidation);
      }
      return;
    }
    setValidation(newValidation);
  };
  const { debounce: handleNumberValid } = useDebounceCallback(
    (value, validation, information) => {
      checkNumberValid(value, validation, information);
    },
    0,
  );

  const { debounce: handleAllowCheckAccountNumber } = useDebounceCallback(
    (validation) => {
      if (validation[field.name]) {
        setChecking(false);
      } else {
        setChecking(true);
      }
    },
    0,
  );

  const { debounce: handleStringTrim } = useDebounceCallback(
    (information, setInformation, setNumberMaskStr) => {
      let stringTrimmed = stringUtils.trimAll(
        information[field.name],
        numberRegex,
      );

      let newInformation = {
        ...information,
        [field.name]: stringTrimmed,
      };
      lastSelfWrittenValueRef.current = stringTrimmed;
      setInformation(newInformation);

      const numberUnmaskStr = getNumberUnmaskStr(
        stringTrimmed,
        numberFormat,
        true,
        true,
        isAllowSpecificCharacter,
      );
      const numberStr = formatter.mask(numberUnmaskStr, numberFormat);
      setNumberMaskStr(numberStr);
    },
    0,
  );

  const handleOnChange = (e, isStringTrim = false) => {
    let { value, name } = e.target;
    let valueTrim = value;

    if (!isDisableTrim) {
      valueTrim = value.trim();
    }

    if (valueTrim) {
      valueTrim = elementHelper.convertSpecificCharactersInput(
        fieldType,
        valueTrim,
      );
    }

    if (isApiEnabled) {
      setChecking(false);
    }

    const numberUnmaskStr = getNumberUnmaskStr(
      valueTrim,
      numberFormat,
      true,
      true,
      isAllowSpecificCharacter,
    );

    // Typing past a fully-masked field's capacity (e.g. a complete 6-digit
    // BSB, mask ["###-###"]) truncates the mask's own output back to the
    // exact value already stored -- nothing was actually edited, so there
    // is nothing for the validation-replacement rules below to legitimately
    // re-evaluate. Returning here leaves an existing message (e.g. a failed
    // async BSB check) untouched; any transition that DOES change the value
    // (a shorter partial value, a genuinely different final value, a full
    // clear) still goes through the normal reset/replace logic below.
    if (numberUnmaskStr === information[name]) return;

    if (numberUnmaskStr?.length > numberMaxLength) return;

    if (!numberUnmaskStr) {
      const newInfomation = {
        ...information,
        [name]: '',
      };
      lastSelfWrittenValueRef.current = '';
      setInformation(newInfomation);

      let newValidation = {
        ...validation,
        [name]: '',
      };

      if (isConfigVisible && isConfigRequired && !numberUnmaskStr) {
        newValidation = {
          ...newValidation,
          [name]: field.msgRequired,
        };
      }

      setValidation(newValidation);

      setNumberMaskStr('');

      return;
    }

    let newInformation = {
      ...information,
    };

    let newValidation = {
      ...validation,
    };

    const numberStr = formatter.mask(numberUnmaskStr, numberFormat);
    setNumberMaskStr(numberStr);

    newValidation = {
      ...newValidation,
      [name]: '',
    };

    newInformation = {
      ...newInformation,
      [name]: numberUnmaskStr,
    };

    lastSelfWrittenValueRef.current = numberUnmaskStr;

    if (isFunction(checkAccountExisted)) {
      if (!isEmpty(accountNumberExisteds)) {
        newValidation = checkAccountExisted(
          accountNumberExisteds,
          newInformation,
          newValidation,
        );
      }
    }

    if (isConfigVisible && numberUnmaskStr) {
      if (numberUnmaskStr.length < numberMinLength) {
        newValidation = {
          ...newValidation,
          [field.name]: field.msgInvalid,
        };
      }
    }

    handleAllowCheckAccountNumber(newValidation);

    if (isStringTrim) {
      handleStringTrim(newInformation, setInformation, setNumberMaskStr);
    } else {
      setInformation(newInformation);
    }

    setValidation(newValidation);

    setSaveDisableInit && setSaveDisableInit(false);
  };

  const handleOnBlur = (e) => {
    if (!isAllowSpecificCharacter) return;
    const isStringTrim = true;
    if (validation[field.name]) return;
    handleOnChange(e, isStringTrim);
  };

  const handleFormLabelOnClick = (e, name) => {
    e.preventDefault();

    if (isDesktop) {
      const inputEle = document.getElementsByName(name)[0];

      if (inputEle) {
        inputEle.focus();
      }
    }
  };

  const getMessageValidation = (validation) => {
    const numberUnmaskStr = getNumberUnmaskStr(
      numberMaskStr,
      numberFormat,
      true,
      true,
      isAllowSpecificCharacter,
    );

    if (
      numberMinLength &&
      numberUnmaskStr.length <= numberMinLength &&
      numberMinLength === numberMaxLength
    ) {
      return stringUtils.replaceKeyword(t(validation[field.name]), [
        {
          key: 'range',
          value: `${numberMaxLength}`,
        },
      ]);
    }

    if (
      numberMinLength &&
      numberUnmaskStr.length < numberMinLength &&
      numberMinLength < numberMaxLength
    ) {
      return stringUtils.replaceKeyword(t(validation[field.name]), [
        {
          key: 'range',
          value: `${numberMinLength}-${numberMaxLength}`,
        },
      ]);
    }

    return stringUtils.replaceKeyword(t(validation[field.name]), [
      {
        key: 'range',
        value: numberMaxLength,
      },
    ]);
  };

  return (
    <FormGroupStyled
      style={
        isMobile || isTablet
          ? { ...customFormInputStyleMobile[field.name], ...styles }
          : { ...customFormInputStyle[field.name], ...styles }
      }
    >
      <FormLabel onClick={(e) => handleFormLabelOnClick(e, field.name)}>
        {label}
        {(isConfigRequired ?? field.isRequired) && <span>*</span>}
        {field.tooltip && (
          <>
            {isMobile || isTablet ? (
              <TooltipCommonMobile
                text={t(field.tooltip)}
                name={field.name}
                label={t(field.label)}
              />
            ) : (
              <TooltipCommon text={t(field.tooltip)} />
            )}
          </>
        )}
      </FormLabel>
      <FormControl
        ref={ref}
        type={fieldType}
        name={field.name}
        value={numberMaskStr}
        placeholder={placeholder || t(field.placeholder)}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        isInvalid={validation[field.name]}
        readOnly={isReadOnly || field?.isReadOnly}
        inputMode={fieldType === 'tel' ? 'numeric' : 'false'}
        style={inputStyles}
        $isDisabled={isDisabled}
      />
      <FormControlFeedback hidden={false} type="invalid" style={errorStyles}>
        {getMessageValidation(validation)}
      </FormControlFeedback>
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  margin-bottom: 32px;
  position: relative;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 34px;
  }
`;
const FormLabel = styled(Form.Label)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-align: left;

  display: block;
  color: var(--ds-c-grey-dark);

  span {
    color: var(--c-required);
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const FormControl = styled(withInputFieldFocus(Form.Control))`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;

  color: ${(props) =>
    props.$isDisabled ? 'var(--ds-c-grey-neutral)' : 'var(--ds-c-grey-dark)'};
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 12px;
  padding-inline: 10px;
  background: ${(props) =>
    props.$isDisabled ? 'var(--ds-c-grey-disabled)' : 'var(--ds-c-white)'};
  cursor: ${(props) => props.$isDisabled && 'not-allowed'};

  &:focus {
    background: ${(props) =>
      props.$isDisabled && 'var(--ds-c-grey-disabled)!important'};
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
    padding-inline: 10px !important;
  }

  &[readonly] {
    background: ${(props) => props.$isDisabled && 'var(--ds-c-grey-disabled)'};
    border: none;
    background: #ececf0;
    border: 1px solid var(--ds-c-blue-remox-light);
    color: var(--ds-c-grey-neutral);
    cursor: not-allowed;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    text-transform: none;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const FormControlFeedback = styled(Form.Control.Feedback)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;

  color: var(--ds-c-red);
  position: absolute;
  margin-top: 4px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 14px;
    line-height: 17px;
  }
`;

export default FormInputValidationWithFormatAndRegex;
